import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { api, errText, mediaUrl } from "../lib/api";
import { usePlayer } from "../context/PlayerContext";
import { useAuth } from "../context/AuthContext";
import { TrackRow } from "./Explore";
import { Loading, SectionHead, Empty } from "../components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Play, Flame, ListMusic, Disc3, Plus, Trash2, Bookmark } from "lucide-react";
import { toast } from "sonner";
import { AddToPlaylistDialog, AlbumGrid, AlbumViewDialog, PlaylistCard, PlaylistViewDialog } from "../components/musicui";

const GENRES = ["All", "Hip-Hop", "R&B", "Pop", "Electronic", "Rock", "Alternative", "Virtual DJs"];
const TABS = ["For You", "Trending", "New Releases", "Albums", "Genres", "Library"];

export default function Music() {
  const { user } = useAuth();
  const [tab, setTab] = useState("Trending");
  const [genre, setGenre] = useState("All");
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addTrack, setAddTrack] = useState(null);
  const [albums, setAlbums] = useState(null);
  const [openAlbum, setOpenAlbum] = useState(null);
  const player = usePlayer();
  const [sp] = useSearchParams();
  const deepTrack = sp.get("track");
  useEffect(() => {
    if (!deepTrack) return;
    api.get(`/music/track/${deepTrack}`).then(({ data }) => { const t = data.track || data; if (t?.id && t.audio_url) player.playTrack(t, [t]); else toast.error("That song isn't available to play yet"); }).catch(() => toast.error("That song isn't available"));
  }, [deepTrack]); // eslint-disable-line

  useEffect(() => {
    if (tab === "Library") { setLoading(false); return; }
    if (tab === "Albums") { setLoading(true); api.get("/music/albums").then(({ data }) => setAlbums(data)).catch(() => setAlbums([])).finally(() => setLoading(false)); return; }
    setLoading(true);
    const sort = tab === "New Releases" ? "new" : "trending";
    const g = tab === "Genres" ? genre : "All";
    api.get(`/music/tracks?sort=${sort}&genre=${encodeURIComponent(g)}`).then(({ data }) => setTracks(data)).finally(() => setLoading(false));
  }, [tab, genre]);

  const featured = tracks.slice(0, 3);

  return (
    <div className="px-4 md:px-6 py-5">
      <div className="flex items-center gap-2 mb-5 overflow-x-auto hide-scroll">
        {TABS.map((t) => <button key={t} onClick={() => setTab(t)} data-testid={`music-tab-${t.replace(/\s/g, "")}`} className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors ${tab === t ? "bg-white text-black" : "surface-2 text-muted-foreground hover:text-white"}`}>{t}</button>)}
      </div>

      {tab === "Albums" ? (loading || !albums ? <Loading /> : albums.length === 0 ? <Empty icon={Disc3} title="No albums released yet" /> : <><SectionHead title="Albums & EPs" sub="Streams from every song count toward the album total" /><AlbumGrid albums={albums} onOpen={setOpenAlbum} testid="music-albums-grid" /></>) :
       tab === "Library" ? <Library onPlay={player.playTrack} isPlus={user?.plan === "plus" || user?.role === "admin"} /> : (
        <>
          {tab === "Genres" && (
            <div className="flex flex-wrap gap-2 mb-6">
              {GENRES.map((g) => <button key={g} onClick={() => setGenre(g)} className={`px-3.5 py-1.5 rounded-full text-sm border transition-colors ${genre === g ? "border-eblue bg-eblue/15 text-eblue" : "border-white/10 surface-2 text-muted-foreground"}`}>{g}</button>)}
            </div>
          )}
          {loading ? <Loading /> : (
            <>
              {featured.length > 0 && (
                <div className="grid sm:grid-cols-3 gap-4 mb-8">
                  {featured.map((t) => (
                    <button key={t.id} onClick={() => player.playTrack(t, tracks)} className="group relative rounded-2xl overflow-hidden surface card-hover text-left" data-testid={`featured-track-${t.id}`}>
                      <img src={t.cover_url} className="w-full aspect-square object-cover" alt="" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-transparent" />
                      <div className="absolute bottom-0 inset-x-0 p-4"><div className="font-display font-bold truncate">{t.title}</div><div className="text-xs text-white/70 truncate">{t.artist?.display_name}</div></div>
                      <span className="absolute top-3 right-3 w-11 h-11 rounded-full btn-gold flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><Play size={20} /></span>
                    </button>
                  ))}
                </div>
              )}
              <SectionHead title={tab === "Trending" ? "Trending Now" : tab} action={<span className="flex items-center gap-1 text-xs text-gold"><Flame size={14} /> VStar Music</span>} />
              <div className="space-y-2">{tracks.map((t, i) => <TrackRow key={t.id} t={t} rank={i + 1} onPlay={() => player.playTrack(t, tracks)} onAdd={setAddTrack} />)}</div>
            </>
          )}
        </>
      )}

      <AddToPlaylistDialog track={addTrack} onClose={() => setAddTrack(null)} />
      <AlbumViewDialog albumId={openAlbum} onClose={() => setOpenAlbum(null)} />
    </div>
  );
}

function Library({ onPlay, isPlus }) {
  const [playlists, setPlaylists] = useState(null);
  const [albums, setAlbums] = useState(null);
  const [newName, setNewName] = useState("");
  const [editAlbum, setEditAlbum] = useState(null);
  const [openPl, setOpenPl] = useState(null);
  const { user } = useAuth();

  const loadPlaylists = () => api.get("/playlists").then(({ data }) => setPlaylists(data));
  const loadAlbums = () => api.get(`/music/albums?artist_id=${user.id}`).then(({ data }) => setAlbums(data));
  useEffect(() => { loadPlaylists(); loadAlbums(); }, []); // eslint-disable-line

  const create = async () => { if (!newName.trim()) return; try { await api.post("/playlists", { name: newName }); setNewName(""); loadPlaylists(); toast.success("Playlist created"); } catch (e) { toast.error(errText(e)); } };
  const playPlaylist = async (p) => { const { data } = await api.get(`/playlists/${p.id}`); if (!data.tracks?.length) return toast.error("Playlist is empty"); onPlay(data.tracks[0], data.tracks); };
  const createAlbum = async () => {
    const title = window.prompt("Album title"); if (!title) return;
    try { const { data } = await api.post("/music/albums", { title, type: "Album" }); loadAlbums(); setEditAlbum(data.id); } catch (e) { toast.error(errText(e)); }
  };
  const playAlbum = async (id) => { const { data } = await api.get(`/music/album/${id}`); if (!data.tracks?.length) return toast.error("Album has no tracks yet"); onPlay(data.tracks[0], data.tracks); };

  return (
    <div className="space-y-8">
      <div>
        <SectionHead title="Your Playlists" sub="Build playlists and queue them in the global player" />
        <div className="flex gap-2 mb-4 max-w-md"><Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="New playlist name" className="surface-2 border-white/10" data-testid="playlist-name-input" /><Button onClick={create} className="btn-blue rounded-xl" data-testid="create-playlist-btn"><Plus size={16} /></Button></div>
        {!playlists ? <Loading /> : playlists.length === 0 ? <Empty icon={ListMusic} title="No playlists yet" hint="Create one, then add tracks with the + on any song." /> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {playlists.map((p) => (
              <div key={p.id} className="flex items-center gap-2">
                <div className="flex-1 min-w-0"><PlaylistCard p={p} onOpen={setOpenPl} owner onChanged={loadPlaylists} /></div>
                <button onClick={() => playPlaylist(p)} className="w-9 h-9 shrink-0 rounded-full btn-gold flex items-center justify-center" data-testid={`play-playlist-${p.id}`}><Play size={16} /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {isPlus && (
        <div>
          <SectionHead title="Album Studio" sub="Group your releases into albums & EPs with cover art" action={<Button onClick={createAlbum} variant="outline" className="rounded-full border-white/15 bg-white/5 h-9" data-testid="create-album-btn"><Disc3 size={15} className="mr-1" /> New Album</Button>} />
          {!albums ? <Loading /> : albums.length === 0 ? <Empty icon={Disc3} title="No albums yet" hint="Create an album, add cover art and drag in your tracks." /> : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {albums.map((a) => (
                <div key={a.id} className="surface rounded-xl overflow-hidden card-hover group" data-testid={`album-${a.id}`}>
                  <div className="relative aspect-square bg-gradient-to-br from-eblue/20 to-gold/10 flex items-center justify-center">
                    {a.cover_url ? <img src={a.cover_url} className="w-full h-full object-cover" alt="" /> : <Disc3 className="text-eblue w-10 h-10" />}
                    <button onClick={() => playAlbum(a.id)} className="absolute bottom-2 right-2 w-10 h-10 rounded-full btn-gold flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`play-album-${a.id}`}><Play size={18} /></button>
                  </div>
                  <button onClick={() => setEditAlbum(a.id)} className="p-3 w-full text-left" data-testid={`edit-album-${a.id}`}><div className="font-medium text-sm truncate">{a.title}</div><div className="text-xs text-muted-foreground">{a.tracks_count} tracks · {a.type} · {a.total_streams || 0} streams · Edit</div></button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <SavedMusic onPlay={onPlay} />
      <PlaylistViewDialog playlistId={openPl} onClose={() => setOpenPl(null)} owner onChanged={loadPlaylists} />
      <AlbumEditorDialog albumId={editAlbum} onClose={() => setEditAlbum(null)} onSaved={loadAlbums} onPlay={onPlay} />
    </div>
  );
}

function AlbumEditorDialog({ albumId, onClose, onSaved, onPlay }) {
  const { user } = useAuth();
  const [album, setAlbum] = useState(null);
  const [myTracks, setMyTracks] = useState([]);
  const [order, setOrder] = useState([]); // track ids in order
  const [title, setTitle] = useState("");
  const [type, setType] = useState("Album");
  const [cover, setCover] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const coverRef = useRef();

  useEffect(() => {
    if (!albumId) return;
    api.get(`/music/album/${albumId}`).then(({ data }) => { setAlbum(data); setTitle(data.title); setType(data.type || "Album"); setCover(data.cover_url); setOrder(data.track_ids || []); });
    api.get(`/music/artist/${user.id}`).then(({ data }) => setMyTracks(data.tracks || []));
  }, [albumId]); // eslint-disable-line

  const byId = Object.fromEntries(myTracks.map((t) => [t.id, t]));
  const inAlbum = order.map((id) => byId[id]).filter(Boolean);
  const available = myTracks.filter((t) => !order.includes(t.id));

  const addTrack = (id) => setOrder((o) => [...o, id]);
  const removeTrack = (id) => setOrder((o) => o.filter((x) => x !== id));
  const move = (i, d) => setOrder((o) => { const n = [...o]; const j = i + d; if (j < 0 || j >= n.length) return o; [n[i], n[j]] = [n[j], n[i]]; return n; });
  const onDrop = (i) => { if (dragIdx === null || dragIdx === i) return; setOrder((o) => { const n = [...o]; const [m] = n.splice(dragIdx, 1); n.splice(i, 0, m); return n; }); setDragIdx(null); };

  const uploadCover = async (file) => { try { const fd = new FormData(); fd.append("file", file); const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setCover(data.url); } catch (e) { toast.error(errText(e)); } };
  const save = async () => { setBusy(true); try { await api.put(`/music/albums/${albumId}`, { title, type, cover_url: cover, track_ids: order }); toast.success("Album saved"); onSaved(); onClose(); } catch (e) { toast.error(errText(e)); } finally { setBusy(false); } };
  const playNow = () => { const tracks = inAlbum.map((t) => ({ ...t, artist: album?.artist })); if (!tracks.length) return toast.error("Add tracks first"); onPlay(tracks[0], tracks); };

  if (!albumId) return null;
  return (
    <Dialog open={!!albumId} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="glass border-white/10 sm:max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="album-editor">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Disc3 size={18} className="text-eblue" /> Album Studio</DialogTitle><DialogDescription className="sr-only">Edit album cover and tracks</DialogDescription></DialogHeader>
        <div className="grid sm:grid-cols-[160px_1fr] gap-4">
          <div>
            <div className="aspect-square rounded-xl overflow-hidden surface-2 flex items-center justify-center cursor-pointer" onClick={() => coverRef.current?.click()}>
              {cover ? <img src={mediaUrl(cover)} className="w-full h-full object-cover" alt="" /> : <div className="text-xs text-muted-foreground text-center px-2">Click to add cover art</div>}
            </div>
            <input ref={coverRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadCover(e.target.files[0])} data-testid="album-cover-input" />
          </div>
          <div className="space-y-2">
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Album title" className="surface-2 border-white/10" data-testid="album-title-input" />
            <select value={type} onChange={(e) => setType(e.target.value)} className="w-full surface-2 border border-white/10 rounded-md px-3 py-2.5 text-sm">{["Album", "EP", "Single", "Mixtape"].map((t) => <option key={t}>{t}</option>)}</select>
            <Button onClick={playNow} variant="outline" className="rounded-full border-white/15 bg-white/5 h-9 w-full" data-testid="album-play-btn"><Play size={14} className="mr-1" /> Play front-to-back</Button>
          </div>
        </div>

        <div className="mt-2">
          <div className="text-xs text-muted-foreground mb-2">Tracklist (drag to reorder)</div>
          {inAlbum.length === 0 ? <p className="text-sm text-muted-foreground surface-2 rounded-lg p-3">No tracks yet — add from below.</p> : (
            <div className="space-y-1.5">
              {inAlbum.map((t, i) => (
                <div key={t.id} draggable onDragStart={() => setDragIdx(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(i)}
                  className="flex items-center gap-2 surface-2 rounded-lg p-2 cursor-grab active:cursor-grabbing" data-testid={`album-track-${t.id}`}>
                  <span className="font-mono text-gold text-xs w-5 text-center">{i + 1}</span>
                  <img src={t.cover_url} className="w-9 h-9 rounded object-cover" alt="" />
                  <span className="text-sm flex-1 truncate">{t.title}</span>
                  <button onClick={() => move(i, -1)} className="text-muted-foreground hover:text-white px-1">↑</button>
                  <button onClick={() => move(i, 1)} className="text-muted-foreground hover:text-white px-1">↓</button>
                  <button onClick={() => removeTrack(t.id)} className="text-rose-400 px-1" data-testid={`remove-album-track-${t.id}`}><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        {available.length > 0 && (
          <div className="mt-2">
            <div className="text-xs text-muted-foreground mb-2">Your tracks — tap to add</div>
            <div className="flex flex-wrap gap-2">
              {available.map((t) => <button key={t.id} onClick={() => addTrack(t.id)} className="flex items-center gap-2 surface-2 rounded-full pl-1.5 pr-3 py-1.5 hover:border-eblue/40 border border-white/10 text-sm" data-testid={`add-album-track-${t.id}`}><img src={t.cover_url} className="w-6 h-6 rounded-full object-cover" alt="" /> {t.title} <Plus size={13} className="text-eblue" /></button>)}
            </div>
          </div>
        )}
        <Button onClick={save} disabled={busy} className="btn-gold w-full rounded-xl h-11 mt-2" data-testid="album-save-btn">{busy ? "Saving…" : "Save Album"}</Button>
      </DialogContent>
    </Dialog>
  );
}

function SavedMusic({ onPlay }) {
  const [lib, setLib] = useState(null);
  const [openAlbum, setOpenAlbum] = useState(null);
  const [addTrack, setAddTrack] = useState(null);
  useEffect(() => { api.get("/library").then(({ data }) => setLib(data)).catch(() => setLib({ tracks: [], albums: [] })); }, [addTrack, openAlbum]);
  return (
    <div data-testid="library-saved">
      <SectionHead title="Saved Music" sub="Songs and albums you saved" />
      {!lib ? <Loading /> : lib.tracks.length + lib.albums.length === 0 ? <Empty icon={Bookmark} title="Nothing saved yet" hint="Tap + on any song or album and choose Save to library." /> : (
        <div className="space-y-4">
          {lib.albums.length > 0 && <AlbumGrid albums={lib.albums} onOpen={setOpenAlbum} testid="library-saved-albums" />}
          {lib.tracks.length > 0 && <div className="space-y-2" data-testid="library-saved-tracks">{lib.tracks.map((t, i) => <TrackRow key={t.id} t={t} rank={i + 1} onPlay={() => onPlay(t, lib.tracks)} onAdd={setAddTrack} />)}</div>}
        </div>
      )}
      <AlbumViewDialog albumId={openAlbum} onClose={() => setOpenAlbum(null)} />
      <AddToPlaylistDialog track={addTrack} onClose={() => setAddTrack(null)} />
    </div>
  );
}
